/**
 * Client-side real-time safety phrase detection for the ambient scribe.
 * Mirrors the backend detector (ehr-go internal/modules/ai/safety.go) so
 * alerts fire instantly even when transcription runs in the browser and
 * never touches the server mid-visit.
 */
import type { SafetyHit } from "@/api/ai.api";

const SELF_HARM = [
  "suicid", "kill myself", "end my life", "ending my life", "end it all",
  "want to die", "wish i was dead", "wish i were dead", "better off dead",
  "no reason to live", "hurt myself", "harm myself", "self-harm", "self harm",
  "cutting myself", "take all my pills",
];

const HARM_OTHERS = [
  "homicid", "kill him", "kill her", "kill them", "kill someone",
  "hurt someone", "hurt him", "hurt her", "hurt them", "shoot him",
  "shoot her", "shoot them", "make them pay",
];

const OVERDOSE = ["overdose", "overdosed", "od'd", "took too many pills"];

function firstMatch(lower: string, phrases: string[]): string {
  for (const p of phrases) {
    if (lower.includes(p)) return p;
  }
  return "";
}

export function detectSafetyRisk(text: string): SafetyHit[] {
  const lower = text.toLowerCase();
  const hits: SafetyHit[] = [];

  const selfHarm = firstMatch(lower, SELF_HARM);
  if (selfHarm) {
    hits.push({
      category: "self_harm",
      severity: "high",
      phrase: selfHarm,
      message:
        "Possible suicidal ideation or self-harm language detected. Complete a formal safety screening (e.g., C-SSRS) before the patient leaves.",
    });
  }

  const harmOthers = firstMatch(lower, HARM_OTHERS);
  if (harmOthers) {
    hits.push({
      category: "harm_others",
      severity: "high",
      phrase: harmOthers,
      message:
        "Possible homicidal ideation or harm-to-others language detected. Assess intent, plan, and duty-to-warn obligations.",
    });
  }

  const overdose = firstMatch(lower, OVERDOSE);
  if (overdose) {
    hits.push({
      category: "overdose",
      severity: "high",
      phrase: overdose,
      message: "Overdose language detected. Evaluate current medical stability and substance use risk.",
    });
  }

  return hits;
}
